import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { UserPlus, Shield, Users, Eye, EyeOff, Gift } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const EMPTY_USER_FORM = { name: '', email: '', password: '', role: 'member', company: '', phone: '', membership_status: 'trial' };

export function AddUserDialog({ open, onOpenChange, isSuperAdmin, onCreated, initialForm }) {
  const [addForm, setAddForm] = useState(initialForm || { ...EMPTY_USER_FORM });
  const [addLoading, setAddLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);

  // Reset form when dialog opens/closes
  React.useEffect(() => {
    if (open && initialForm) {
      setAddForm(initialForm);
      setShowPw(false);
    }
  }, [open, initialForm]);

  const updateField = (field, value) => setAddForm(prev => ({ ...prev, [field]: value }));

  const handleAddUser = async () => {
    if (!addForm.name.trim()) { toast.error('Name is required'); return; }
    if (!addForm.email.trim()) { toast.error('Email is required'); return; }
    if (addForm.password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setAddLoading(true);
    try {
      await api.adminCreateUser(addForm);
      toast.success(`User "${addForm.name}" created successfully`);
      onOpenChange(false);
      setAddForm({ ...EMPTY_USER_FORM });
      setShowPw(false);
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create user');
    }
    setAddLoading(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md rounded-sm" data-testid="add-user-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-[#0047AB]" /> Add New User
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div>
            <Label className="text-xs font-medium text-slate-700 uppercase tracking-wide">Full Name *</Label>
            <Input value={addForm.name} onChange={e => updateField('name', e.target.value)} placeholder="John Doe" className="mt-1 rounded-sm" data-testid="add-user-name" />
          </div>
          <div>
            <Label className="text-xs font-medium text-slate-700 uppercase tracking-wide">Email *</Label>
            <Input type="email" value={addForm.email} onChange={e => updateField('email', e.target.value)} placeholder="john@company.com" className="mt-1 rounded-sm" data-testid="add-user-email" />
          </div>
          <div>
            <Label className="text-xs font-medium text-slate-700 uppercase tracking-wide">Password *</Label>
            <div className="relative mt-1">
              <Input type={showPw ? 'text' : 'password'} value={addForm.password} onChange={e => updateField('password', e.target.value)} placeholder="Min 6 characters" className="rounded-sm pr-10" data-testid="add-user-password" />
              <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors" data-testid="toggle-password-visibility">
                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div>
            <Label className="text-xs font-medium text-slate-700 uppercase tracking-wide">Role</Label>
            <Select value={addForm.role} onValueChange={v => updateField('role', v)}>
              <SelectTrigger className="mt-1 rounded-sm" data-testid="add-user-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                {isSuperAdmin && <SelectItem value="admin"><div className="flex items-center gap-1.5"><Shield className="w-3.5 h-3.5 text-red-500" /> Admin</div></SelectItem>}
                <SelectItem value="member"><div className="flex items-center gap-1.5"><Users className="w-3.5 h-3.5 text-blue-500" /> Member</div></SelectItem>
                <SelectItem value="viewer"><div className="flex items-center gap-1.5"><Eye className="w-3.5 h-3.5 text-slate-400" /> Viewer</div></SelectItem>
              </SelectContent>
            </Select>
          </div>
          {isSuperAdmin && (
            <div className="flex items-center gap-3 p-3 bg-purple-50 border border-purple-200 rounded-sm">
              <button type="button" onClick={() => updateField('membership_status', addForm.membership_status === 'free' ? 'trial' : 'free')} className={`w-9 h-5 rounded-full transition-colors relative ${addForm.membership_status === 'free' ? 'bg-purple-600' : 'bg-slate-300'}`} data-testid="add-user-free-toggle">
                <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-transform shadow-sm ${addForm.membership_status === 'free' ? 'left-[18px]' : 'left-0.5'}`} />
              </button>
              <div>
                <p className="text-xs font-medium text-purple-800 flex items-center gap-1"><Gift className="w-3.5 h-3.5" /> Free Account</p>
                <p className="text-[10px] text-purple-600">No membership fee — full access with no trial expiry</p>
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-medium text-slate-700 uppercase tracking-wide">Company</Label>
              <Input value={addForm.company} onChange={e => updateField('company', e.target.value)} placeholder="Optional" className="mt-1 rounded-sm" data-testid="add-user-company" />
            </div>
            <div>
              <Label className="text-xs font-medium text-slate-700 uppercase tracking-wide">Phone</Label>
              <Input value={addForm.phone} onChange={e => updateField('phone', e.target.value)} placeholder="Optional" className="mt-1 rounded-sm" data-testid="add-user-phone" />
            </div>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-sm p-3">
            <p className="text-xs text-blue-700">A welcome email with login credentials will be sent to the user automatically.</p>
          </div>
        </div>
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm" data-testid="add-user-cancel">Cancel</Button>
          <Button onClick={handleAddUser} disabled={addLoading} className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm" data-testid="add-user-submit">
            {addLoading ? 'Creating...' : 'Create User'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
